import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Space,
  Tooltip,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  createMenuDiscount,
  updateMenuDiscount,
  deleteMenuDiscount,
  getMenuItems,
} from "../../actions/merchant_actions";
import { getAllRewards } from "../../actions/acc_actions";

const MenuDiscounts = ({ data, refetchMerchant }) => {
  const accUrn = useSelector((state) => state.account.urn);
  const businessUrn = useSelector((state) => state.account.businessUrn);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [skuOptions, setSkuOptions] = useState([]);
  const [rewardOptions, setRewardOptions] = useState([]);
  const [form] = Form.useForm();

  // Fetch SKUs
  const { data: itemsData } = useQuery({
    queryKey: ["menuItems", accUrn],
    queryFn: () => getMenuItems({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  // Fetch Rewards
  const { data: rewardsData, isLoading } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getAllRewards(businessUrn),
    enabled: !!businessUrn,
  });

  // Build SKU options
  useEffect(() => {
    if (itemsData) {
      const skuOpts = itemsData.map((item) => ({
        label: `${item.data.title} (${item.data.sku})`,
        value: item.data.sku,
      }));
      setSkuOptions(skuOpts);
    }
  }, [itemsData]);

  // Build Reward options
  useEffect(() => {
    if (rewardsData) {
      const rewardOpts = rewardsData?.data.map((reward) => ({
        label: reward.name,
        value: reward.slug,
      }));
      setRewardOptions(rewardOpts);
    }
  }, [rewardsData]);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      skus: record.skus,
      reward_type_slug: record.reward_type_slug,
    });
    setIsModalOpen(true);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const payload = {
        skus: values.skus.join(","),
        reward_type_slug: values.reward_type_slug,
      };

      const action = editingRecord
        ? updateMenuDiscount({
            account_urn: accUrn,
            urn: editingRecord.urn,
            data: payload,
          })
        : createMenuDiscount({
            account_urn: accUrn,
            data: [payload],
          });

      action
        .then(() => {
          message.success(
            editingRecord ? "Discount updated!" : "Discount created!"
          );
          refetchMerchant();
          setIsModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        })
        .catch(() => {
          message.error(
            editingRecord
              ? "Failed to update discount."
              : "Failed to create discount."
          );
        });
    });
  };

  const handleDelete = (record) => {
    deleteMenuDiscount({
      account_urn: accUrn,
      urn: record.urn,
    })
      .then(() => {
        message.success("Discount deleted!");
        refetchMerchant();
      })
      .catch(() => {
        message.error("Failed to delete discount.");
      });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (id) => (
        <Tooltip title={id}>
          <span
            style={{
              maxWidth: 180,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {id}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      render: (mode) => <Tag color="blue">{mode}</Tag>,
    },
    {
      title: "SKUs",
      dataIndex: "skus",
      key: "skus",
      render: (skus) =>
        skus.map((sku) => (
          <Tag key={sku} color="purple">
            {sku}
          </Tag>
        )),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="green">{type}</Tag>,
    },
    {
      title: "Percent",
      dataIndex: "percent",
      key: "percent",
      render: (percent) => `${percent * 100}%`,
    },
    { title: "Selection", dataIndex: "selection", key: "selection" },
    { title: "Applies To", dataIndex: "applies_to", key: "applies_to" },
    { title: "Combine With", dataIndex: "combine_with", key: "combine_with" },
    {
      title: "Reward Slug",
      dataIndex: "reward_type_slug",
      key: "reward_type_slug",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record)}>
            Update
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this discount?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "1rem" }}>
      <Space
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Add Discount
        </Button>
      </Space>

      <Table
        rowKey={(record) => record.urn || record.id}
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
        size="middle"
      />

      <Modal
        title={editingRecord ? "Update Discount" : "Create Discount"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        okText={editingRecord ? "Update" : "Create"}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="SKU"
            name="skus"
            rules={[
              { required: true, message: "Please select at least one SKU" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select products by SKU"
              options={skuOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Reward Type"
            name="reward_type_slug"
            rules={[{ required: true, message: "Please select a reward" }]}
          >
            <Select
              placeholder="Select a reward type"
              options={rewardOptions}
              loading={isLoading}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuDiscounts;
